import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Helpers } from '../../../helpers/Helpers';
import { GetInvoiceByIdResponseInvoiceDetail, GetInvoiceByIdResponseInvoiceDetailWithholding } from '../../../models/response/invoice/GetInvoiceByIdResponse';
import { storeActivityIndicator, storeAreYouSureModalCallback } from '../../../redux/reducers/authSlice';
import { ConstantService } from '../../../services/ConstantService';
import { ServiceService } from '../../../services/ServiceService';
import { KTCard } from '../../../_metronic/helpers/components/KTCard';
import { KTCardBody } from '../../../_metronic/helpers/components/KTCardBody';
import Box from '../../Box';
import CustomSingleSelect, { CustomSingleSelectModel } from '../../CustomSingleSelect';
import toast from "react-hot-toast";
import { GetServiceByIdResponse } from '../../../models/response/service/GetServiceByIdResponse';
import CustomNumberInput from '../../CustomNumberInput';
import CustomTextInput from '../../CustomTextInput';
import { WithholdingService } from '../../../services/WithholdingService';
import { GetWithholdingByIdResponse, GetWithholdingByIdResponseData } from '../../../models/response/withholding/GetWithholdingByIdResponse';
import { faturaBrutToplami, gorunenTevkifatOrani, tevkifatlariYenidenHesapla, tevkifatTutari } from '../../../helpers/tevkifatHesabi';

interface Props {
    invoiceDetailsRef: any
    invoiceDetails: GetInvoiceByIdResponseInvoiceDetail[]
    setInvoiceDetails: React.Dispatch<React.SetStateAction<GetInvoiceByIdResponseInvoiceDetail[]>>
    isIncome: boolean
    isCustomerWithholding: boolean
    invoiceDate: string | undefined
}

const CommonInvoiceInvoiceDetails = ({
    invoiceDetailsRef,
    invoiceDetails,
    setInvoiceDetails,
    isIncome,
    isCustomerWithholding,
    invoiceDate
}: Props) => {
    const dispatch = useDispatch();
    const [invoiceDetailId, setInvoiceDetailId] = useState<string | undefined>(undefined);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedService, setSelectedService] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [unitPrice, setUnitPrice] = useState<string | undefined>(undefined);
    const [description, setDescription] = useState<string | undefined>(undefined);
    const [quantity, setQuantity] = useState<string | undefined>('1');
    const [selectedInvoiceException, setSelectedInvoiceException] = useState<CustomSingleSelectModel | undefined>(undefined);
    const [taxRate, setTaxRate] = useState<string | undefined>(undefined);
    const [selectedWithholding, setSelectedWithholding] = useState<GetInvoiceByIdResponseInvoiceDetailWithholding | undefined>(undefined);
    const [totalWithoutTaxPrice, setTotalWithoutTaxPrice] = useState<string | undefined>(undefined);
    const [totalPrice, setTotalPrice] = useState<string | undefined>(undefined);
    const [taxPrice, setTaxPrice] = useState<string | undefined>(undefined);
    const [withholdingPrice, setWithholdingPrice] = useState<string | undefined>(undefined);
    // #510: düzenlenen satır tevkifat dışıysa (kira sabit gelir / Yurtiçi) hizmette oran olsa da tevkifat uygulanmaz.
    const [tevkifatDisi, setTevkifatDisi] = useState<boolean>(false);

    const setItemSelected = (_id: string) => {
        setInvoiceDetails(invoiceDetails.map(function (x) {
            if (x.invoiceDetailId == _id)
                x.isSelected = !x.isSelected;
            return x;
        }))
    }

    const deleteItems = () => {
        // Satir silinince fatura brut toplami degisir: kalan satirlarin tevkifati yeniden hesaplanir.
        setInvoiceDetails(tevkifatlariYenidenHesapla(invoiceDetails.filter(x => !x.isSelected), isCustomerWithholding));
    }

    const saveItem = () => {
        if (Helpers.isEmpty(invoiceDetailId)) {
            var o: GetInvoiceByIdResponseInvoiceDetail = {
                invoiceDetailId: Helpers.generateUUID(),
                isSelected: false,
                description: description,
                quantity: quantity,
                serviceId: selectedService?.value,
                serviceName: selectedService?.label,
                taxRate: taxRate,
                unitPrice: unitPrice,
                exceptionId: selectedInvoiceException?.value,
                exceptionName: selectedInvoiceException?.label,
                taxPrice: taxPrice ?? '',
                totalPrice: totalPrice ?? '',
                totalWithoutTaxPrice: totalWithoutTaxPrice ?? '',
                withholdingId: selectedWithholding?.withholdingId,
                withholdingName: selectedWithholding?.withholdingRateName,
                withholdingKey: selectedWithholding?.withholdingRateKey,
                withholdingPrice: withholdingPrice,
                withholding: selectedWithholding,
            }

            // Satir eklenince fatura brut toplami degisir: butun satirlarin tevkifati yeniden hesaplanir.
            setInvoiceDetails(tevkifatlariYenidenHesapla([...invoiceDetails, o], isCustomerWithholding))
        }
        else {
            setInvoiceDetails(tevkifatlariYenidenHesapla(invoiceDetails.map(function (x) {
                if (x.invoiceDetailId == invoiceDetailId) {
                    x.isSelected = false;
                    x.description = description;
                    x.quantity = quantity;
                    x.serviceId = selectedService?.value;
                    x.serviceName = selectedService?.label;
                    x.taxRate = taxRate;
                    x.unitPrice = unitPrice;
                    x.exceptionId = selectedInvoiceException?.value;
                    x.exceptionName = selectedInvoiceException?.label;
                    x.taxPrice = taxPrice ?? '';
                    x.totalPrice = totalPrice ?? '';
                    x.totalWithoutTaxPrice = totalWithoutTaxPrice ?? ''
                    x.withholdingId = selectedWithholding?.withholdingId;
                    x.withholdingName = selectedWithholding?.withholdingRateName;
                    x.withholdingKey = selectedWithholding?.withholdingRateKey;
                    x.withholdingPrice = withholdingPrice;
                    x.withholding = selectedWithholding;
                    x.tevkifatDisi = tevkifatDisi;
                }

                return x;
            }), isCustomerWithholding))
        }

        setInvoiceDetailId('');
        setSelectedService(undefined);
        setUnitPrice('');
        setQuantity('1');
        setTaxRate('');
        setDescription('');
        setSelectedInvoiceException(undefined);
        setTotalWithoutTaxPrice('');
        setTotalPrice('');
        setTaxPrice('');
        setSelectedWithholding(undefined);
        setWithholdingPrice('');
        setTevkifatDisi(false);
        setIsOpen(false);
        scrollTo();
    }

    const setUpdateItem = () => {
        var o = invoiceDetails.filter(x => x.isSelected)[0];

        setInvoiceDetailId(o.invoiceDetailId);
        setSelectedService(o.serviceId ? { label: o.serviceName + '', value: o.serviceId } : undefined);
        setUnitPrice(o.unitPrice);
        setQuantity(o.quantity);
        setTaxRate(o.taxRate)
        setDescription(o.description);
        setSelectedInvoiceException(o.exceptionId && o.exceptionName ? { label: o.exceptionName, value: o.exceptionId } : undefined);
        setTotalWithoutTaxPrice(o.totalWithoutTaxPrice);
        setTotalPrice(o.totalPrice);
        setTaxPrice(o.taxPrice);
        setSelectedWithholding(o.withholding);
        setWithholdingPrice(o.withholdingPrice);
        setTevkifatDisi(!!o.tevkifatDisi);
        setIsOpen(true);
        scrollTo();
    }

    const cancelAdding = () => {
        if (!isOpen) {
            setIsOpen(true);
            scrollTo();
            return;
        }

        setInvoiceDetailId('');
        setSelectedService(undefined);
        setUnitPrice('');
        setQuantity('1');
        setTaxRate('');
        setDescription('');
        setSelectedInvoiceException(undefined);
        setTotalWithoutTaxPrice('');
        setTotalPrice('');
        setTaxPrice('');
        setSelectedWithholding(undefined);
        setWithholdingPrice('');
        setTevkifatDisi(false);
        setIsOpen(false);
        scrollTo();
    }

    const selectAll = () => {
        var isAllSelected = invoiceDetails.length == invoiceDetails.filter(x => x.isSelected).length;

        setInvoiceDetails(invoiceDetails.map(function (x) {
            x.isSelected = !isAllSelected;
            return x;
        }))
    }

    const scrollTo = () => {
        var headerOffset = 120;
        var elementPosition = invoiceDetailsRef.current.getBoundingClientRect().top;
        var offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
        });
    }

    const groupInvoices = () => {
        const groupedInvoiceDetails: GetInvoiceByIdResponseInvoiceDetail[] = [];

        for (const item of invoiceDetails) {
            const existing = groupedInvoiceDetails.find(x => x.serviceId === item.serviceId &&
                x.taxRate === item.taxRate &&
                x.withholdingId == item.withholdingId &&
                !!x.tevkifatDisi == !!item.tevkifatDisi
            );

            if (existing) {
                existing.quantity = (Helpers.toNumber(existing.quantity) + Helpers.toNumber(item.quantity)).toFixed(4);
                existing.totalPrice = (Helpers.toNumber(existing.totalPrice) + Helpers.toNumber(item.totalPrice)).toFixed(4);
                existing.taxPrice = (Helpers.toNumber(existing.taxPrice) + Helpers.toNumber(item.taxPrice)).toFixed(4);
                existing.totalWithoutTaxPrice = (Helpers.toNumber(existing.totalWithoutTaxPrice) + Helpers.toNumber(item.totalWithoutTaxPrice)).toFixed(4);
                existing.withholdingPrice = (Helpers.toNumber(existing.withholdingPrice) + Helpers.toNumber(item.withholdingPrice)).toFixed(4);
                existing.description = (existing.description ?? "") + ", " + item.description;

                // Birim fiyatı yeniden hesapla
                existing.unitPrice = (Helpers.toNumber(existing.totalWithoutTaxPrice) / Helpers.toNumber(existing.quantity)).toFixed(4);
            } else {
                groupedInvoiceDetails.push({
                    ...item
                });
            }
        }

        for (var i = 0; i < groupedInvoiceDetails.length; i++) {
            var arr = (groupedInvoiceDetails[i].description ?? "").split(',').filter(x => !Helpers.isEmpty(x)).map(x => x.trim());
            arr = [...new Set(arr)];

            groupedInvoiceDetails[i].description = arr.join(", ");
        }

        setInvoiceDetails(groupedInvoiceDetails);
    }

    const onServiceSelected = async (_selectedItem: CustomSingleSelectModel | undefined) => {
        setSelectedService(_selectedItem);
        setTaxRate(_selectedItem?.additionalValue1 ?? '');

        if (!_selectedItem) {
            calculateFields(undefined, undefined, unitPrice, quantity, undefined);
            return;
        }

        try {
            dispatch(storeActivityIndicator(true));

            var response: GetServiceByIdResponse = await ServiceService.getServiceById(_selectedItem.value);

            if (!response.isSuccess) {
                toast.error(response.message)
                return;
            }

            var _selectedWithholding: GetWithholdingByIdResponseData | undefined = undefined;

            if (response.data.withholdingRateId && invoiceDate && !tevkifatDisi) {
                var response2: GetWithholdingByIdResponse = await WithholdingService.getWithholdingByWithholdingRateIdAndDate(response.data.withholdingRateId, invoiceDate);

                if (!response2.isSuccess) {
                    toast.error(response2.message)
                    return;
                }

                if (!response2.data) {
                    toast("Seçili hizmet kaleminde " + response.data.withholdingRateName + " tevkifat oranı tanımlıdır; ancak fatura tarihi için aktif tevkifat türü/limit tanımı bulunamadı. Lütfen Tevkifat Türü Tanımları veya tarih aralığını kontrol ediniz", { icon: '⚠️' });
                    return;
                }

                _selectedWithholding = response2.data;
            }

            calculateFields(_selectedItem, _selectedWithholding, unitPrice, quantity, _selectedItem?.additionalValue1);
        } catch (error: any) {
            toast.error(error)
        }
        finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const calculateFields = (
        _selectedService: CustomSingleSelectModel | undefined,
        _selectedWithholding: GetInvoiceByIdResponseInvoiceDetailWithholding | undefined,
        _unitPrice: string | undefined,
        _quantity: string | undefined,
        _taxRate: string | undefined
    ) => {
        setUnitPrice(_unitPrice);
        setQuantity(_quantity);
        setTotalWithoutTaxPrice((Helpers.toNumber(_unitPrice) * Helpers.toNumber(_quantity)).toFixed(4))

        var _totalPrice = ((Helpers.toNumber(_unitPrice) * Helpers.toNumber(_quantity)) + ((Helpers.toNumber(_unitPrice) * Helpers.toNumber(_quantity)) * (Helpers.toNumber(_taxRate?.toString()?.replace('%', '')) / 100))).toFixed(4);

        var _taxPrice = ((Helpers.toNumber(_unitPrice) * Helpers.toNumber(_quantity)) * (Helpers.toNumber(_taxRate?.toString()?.replace('%', '')) / 100)).toFixed(4);

        // Alt limit: faturanin tevkifat oncesi brut toplami (diger satirlar + bu satirin KDV dahil tutari).
        var brutToplam = faturaBrutToplami(invoiceDetails.filter(x => x.invoiceDetailId != invoiceDetailId)) + Helpers.toNumber(_totalPrice);
        var _withholdingPrice = tevkifatTutari(_taxPrice, _selectedWithholding, brutToplam, isCustomerWithholding);

        setSelectedWithholding(_selectedWithholding);
        setWithholdingPrice(_withholdingPrice);
        setTotalPrice((Helpers.toNumber(_totalPrice) - Helpers.toNumber(_withholdingPrice)).toFixed(4));
        setTaxPrice(_taxPrice);
    }

    return (
        <div className='containerPart' ref={invoiceDetailsRef}>
            <KTCard className="ebbe-card mt-10">
                <KTCardBody>
                    <h4 className="mb-10 text-black">Fatura Detayları</h4>

                    {
                        isOpen && (
                            <React.Fragment>
                                <div className="row">
                                    <div className="col-md-4">
                                        <div>
                                            <label htmlFor="exampleFormControlInput1" className="form-label">Hizmet Kalemi</label>

                                            <CustomSingleSelect
                                                selectedValue={selectedService}
                                                onChange={onServiceSelected}
                                                onMenuOpen={() => ServiceService.getServiceList(isIncome)}
                                                labelName='name'
                                                valueName='serviceId'
                                                additionalValue1Name={isIncome ? 'incomePercentageName' : 'outcomePercentageName'}
                                            />
                                        </div>

                                        <div className="mt-5">
                                            <CustomNumberInput labelText='KDV %' value={taxRate} setValue={setTaxRate} isDisabled />
                                        </div>

                                        <div className='mt-5'>
                                            <CustomTextInput labelText='Tevkifat Oranı' value={gorunenTevkifatOrani(selectedWithholding?.withholdingRateName, withholdingPrice)} setValue={undefined} isDisabled />
                                        </div>

                                        <div className="mt-5">
                                            <label htmlFor="exampleFormControlInput1" className="form-label">İstisna Kodu</label>

                                            <CustomSingleSelect
                                                selectedValue={selectedInvoiceException}
                                                onChange={setSelectedInvoiceException}
                                                onMenuOpen={() => ConstantService.getConstantList('INVOICE_EXCEPTION')}
                                                labelName='value'
                                                valueName='constantId'
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <div>
                                            <CustomNumberInput labelText='Birim Fiyat' value={unitPrice} setValue={(value: string) => calculateFields(selectedService, selectedWithholding, value, quantity, taxRate)} />
                                        </div>

                                        <div className="mt-5">
                                            <CustomNumberInput labelText='Toplam Tutar' value={totalWithoutTaxPrice} setValue={setTotalWithoutTaxPrice} isDisabled />
                                        </div>

                                        <div className="mt-5">
                                            <CustomNumberInput labelText='Tevkifat Tutarı' value={withholdingPrice} setValue={setWithholdingPrice} isDisabled />
                                        </div>

                                        <div className="mt-5">
                                            <CustomTextInput labelText='Açıklama' value={description} setValue={setDescription} />
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <div>
                                            <CustomNumberInput labelText='Miktar' value={quantity} setValue={(value: string) => calculateFields(selectedService, selectedWithholding, unitPrice, value, taxRate)} />
                                        </div>

                                        <div className="mt-5">
                                            <CustomNumberInput labelText='KDV Tutar' value={taxPrice} setValue={setTaxPrice} isDisabled />
                                        </div>

                                        <div className="mt-5">
                                            <CustomNumberInput labelText='Brüt Tutar' value={totalPrice} setValue={setTotalWithoutTaxPrice} isDisabled />
                                        </div>
                                    </div>
                                </div>

                                <div className="row mt-5">
                                    <div className="col-md-12">
                                        <Box alignItems="flex-end">
                                            <Box onClick={saveItem} className="actionIcon p-2 bg-success" justifyContent="center" flexDirection="row" borderRadius="4px">
                                                <Box mr="5px" justifyContent="center">
                                                    <i className="text-white fa fa-check" />
                                                </Box>
                                                <Box justifyContent="center">
                                                    <span style={{ fontWeight: 600 }} className="text-white">Kaydet</span>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </div>
                                </div>

                                <Box className='ebbe-hr m-0 mt-5'>&nbsp;</Box>
                            </React.Fragment>
                        )
                    }

                    <Box className={isOpen ? "mt-5" : ""} flexDirection="row">
                        <Box onClick={cancelAdding} className={"actionIcon p-2 " + (!isOpen ? "bg-success" : "")} bg={isOpen ? 'orange' : undefined} justifyContent="center" flexDirection="row" borderRadius="4px">
                            <Box mr="5px" justifyContent="center">
                                <i className={"text-white fa " + (!isOpen ? 'fa-plus' : 'fa-times')} />
                            </Box>
                            <Box justifyContent="center">
                                <span style={{ fontWeight: 600 }} className="text-white">{!isOpen ? 'Ekle' : 'İptal Et'}</span>
                            </Box>
                        </Box>

                        <Box onClick={invoiceDetails.filter(x => x.isSelected).length == 1 ? setUpdateItem : undefined} opacity={invoiceDetails.filter(x => x.isSelected).length == 1 ? "1" : "0.5"} ml="10px" className="actionIcon p-2 bg-primary" justifyContent="center" flexDirection="row" borderRadius="4px">
                            <Box mr="5px" justifyContent="center">
                                <i className="text-white fa fa-edit" />
                            </Box>
                            <Box justifyContent="center">
                                <span style={{ fontWeight: 600 }} className="text-white">Güncelle</span>
                            </Box>
                        </Box>

                        <Box onClick={invoiceDetails.filter(x => x.isSelected).length > 0 ? () => dispatch(storeAreYouSureModalCallback(deleteItems)) : undefined} opacity={invoiceDetails.filter(x => x.isSelected).length > 0 ? "1" : "0.5"} ml="10px" className="actionIcon p-2 bg-danger" justifyContent="center" flexDirection="row" borderRadius="4px">
                            <Box mr="5px" justifyContent="center">
                                <i className="text-white fa fa-trash" />
                            </Box>
                            <Box justifyContent="center">
                                <span style={{ fontWeight: 600 }} className="text-white">Sil</span>
                            </Box>
                        </Box>

                        <Box onClick={groupInvoices} ml="10px" className="actionIcon p-2" bg='orange' justifyContent="center" flexDirection="row" borderRadius="4px">
                            <Box mr="5px" justifyContent="center">
                                <i className="text-white fa fa-list" />
                            </Box>
                            <Box justifyContent="center">
                                <span style={{ fontWeight: 600 }} className="text-white">Grupla</span>
                            </Box>
                        </Box>
                    </Box>

                    <div className="row mt-5">
                        <div className="col-md-12">
                            <div className='table-responsive'>
                                <table id='kt_table_users' className='table align-middle table-row-dashed dataTable no-footer'>
                                    <thead>
                                        <tr className='text-start text-muted fw-bolder text-uppercase'>
                                            <th style={{ width: 1 }}>
                                                <div className='d-flex'>
                                                    <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                        <input onChange={selectAll} className='form-check-input' type='checkbox' checked={invoiceDetails.filter(x => x.isSelected).length > 0 && invoiceDetails.filter(x => x.isSelected).length == invoiceDetails.length} />
                                                    </label>
                                                </div>
                                            </th>
                                            <th>Hizmet Kalemi</th>
                                            <th>Birim Fiyat</th>
                                            <th>Miktar</th>
                                            <th>KDV %</th>
                                            <th>Toplam Tutar</th>
                                            <th>KDV Tutar</th>
                                            <th>Tevkifat Oranı</th>
                                            <th>Tevkifat Tutarı</th>
                                            <th>Brüt Tutar</th>
                                            <th>İstisna Kodu</th>
                                            <th>Açıklama</th>
                                        </tr>
                                    </thead>
                                    <tbody className='text-gray-600 fw-bold'>
                                        {
                                            invoiceDetails.length == 0 && (
                                                <tr>
                                                    <td className="text-center" colSpan={99}>Kayıt bulunamadı.</td>
                                                </tr>
                                            )
                                        }
                                        {
                                            invoiceDetails.map((item, index) =>
                                                <tr>
                                                    <td style={{ width: 1 }}>
                                                        <div className='d-flex'>
                                                            <label className='form-check form-check-sm form-check-custom form-check-solid'>
                                                                <input onChange={() => setItemSelected(item.invoiceDetailId)} className='form-check-input' type='checkbox' checked={item.isSelected} />
                                                            </label>
                                                        </div>
                                                    </td>
                                                    <td>{item.serviceName}</td>
                                                    <td className='custom-text-right'>{Helpers.correctPrice(item.unitPrice, 2)}</td>
                                                    <td className='custom-text-right'>{Helpers.correctPrice(item.quantity)}</td>
                                                    <td>{Helpers.correctPrice(item.taxRate, 2)}</td>
                                                    <td className='custom-text-right'>{Helpers.correctPrice(item.totalWithoutTaxPrice, 2)}</td>
                                                    <td className='custom-text-right'>{Helpers.correctPrice(item.taxPrice, 2)}</td>
                                                    <td>{gorunenTevkifatOrani(item.withholdingName, item.withholdingPrice)}</td>
                                                    <td className='custom-text-right'>{Helpers.correctPrice(item.withholdingPrice, 2)}</td>
                                                    <td className='custom-text-right'>{Helpers.correctPrice(item.totalPrice, 2)}</td>
                                                    <td>{item.exceptionName}</td>
                                                    <td>{item.description}</td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </KTCardBody>
            </KTCard>
        </div>
    )
}

export default CommonInvoiceInvoiceDetails;